import { AbstractPageBuilderService } from '../../PageBuilder/services/abstract-page-builder.service';
import { OnDestroy, Injectable } from '@angular/core';
import { ReportsService } from './reports.service';
import { BlockModel } from '../../PageBuilder/models/block.model';
import { reportsBlockTypes } from '../models/reports-block-types.model';
import { Gridster, GridsterConfig, GridType } from 'angular-gridster2';
import { ChartBlockComponent } from '../components/ChartBlock/chart-block.component';
import { StylesService } from '../../Styles/services/styles.service';
import { TwigRendererService } from '../../PageBuilder/services/twig-renderer.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { ReportModel } from '../models/report.model';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { reportChartFixedCellHeight, reportChartFixedCellWidth, reportChartMargin } from '@Common/Charts/helpers/chart-size.helper';
import { BehaviorSubject, Observable } from 'rxjs';
import { RecentColorsService } from '../../Forms/components/AdvancedColorPickerPopover/service/recent-colors.service';


@Injectable()
export class ReportsPageBuilderService extends AbstractPageBuilderService implements OnDestroy {
    private blockChartMap: Map<BlockModel, ChartBlockComponent>;

    readonly isGrid: boolean = true;
    readonly gridSettings: GridsterConfig = {
        gridType: GridType.Fixed,
        margin: reportChartMargin,
        maxCols: 12,
        outerMargin: true,
        outerMarginBottom: 30,
        outerMarginRight: 30,
        outerMarginLeft: 0,
        outerMarginTop: 0,
        fixedColWidth: reportChartFixedCellWidth,
        fixedRowHeight: reportChartFixedCellHeight,
        maxItemCols: 10,
        minItemCols: 2,
        maxItemRows: 6,
        minItemRows: 2,
        draggable: {
            enabled: true,
            dragHandleClass: 'agpr-block-dragHandle',
            ignoreContent: true
        },
        resizable: {
            enabled: true
        },
        displayGrid: 'always',
        pushItems: true,
        scrollToNewItems: true,
        pushDirections: {north: true, east: true, south: true, west: true},
        keepFixedHeightInMobile: true,
        keepFixedWidthInMobile: true,
        mobileBreakpoint: 0,
        initCallback: (gridster) => this.getGridsterAPI(gridster),
        itemChangeCallback: (blockModel: BlockModel) => {
            this.updateBlockOrder();
            this.resizedBlock$.next(blockModel);
        }
    };

    public gridsterAPI;
    public currentReport: ReportModel;

    protected resizedBlock$: BehaviorSubject<BlockModel> = new BehaviorSubject(null);

    /**
     * Class Constructor
     */
    constructor(
        public reportsService: ReportsService,
        public stylesService: StylesService,
        public twigRendererService: TwigRendererService,
        public localizationService: LocalizationService,
        private recentColorsService: RecentColorsService,
    ) {
        super(stylesService, twigRendererService, localizationService);

        this.subscriptions = [];
        this.setSubscriptions();
        this.blockChartMap = new Map();
        this.setBlockTypes(reportsBlockTypes);
    }

    ngOnDestroy() {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
    }

    /**
     * Sets subscriptions, pushing to array for easy cleanup later
     */
    private setSubscriptions(): void {
        this.subscriptions.push(
            this.reportsService.getCurrentReport().subscribe(
                currentReport => {
                    if (!currentReport) {
                        // TODO: loading here
                        this.setBlocks([]);
                        return;
                    }

                    currentReport.blocks.forEach(block => this.setMaxMinDimensions(block));

                    // Don't call this.setBlocks because it calls setCurrentReportBlocks and results in a infinite loop
                    super.setBlocks(currentReport.blocks);
                    this.currentReport = currentReport;
                    this.updateBlockOrder();
                    this.recentColorsService.setContext(currentReport.blocks, {});
                }
            )
        );
    }

    /**
     * Updates the list of blocks
     */
    setBlocks(blocks: BlockModel[]): void {
        super.setBlocks(blocks);

        this.reportsService.setCurrentReportBlocks(this.blocks);
    }

    /**
     * Updates the blocks when changes occur so that things are notified
     * @NOTE Called by the BlockForm component when a form field value is changed
     */
    formFieldUpdated(): void {
        // using super here prevents the reports service from causing a reset in the selected block
        super.setBlocks(this.blocks);
        this.notifyBlockDisplayElementChanged(this.selectedBlock);
    }

    /**
     * These maps are to give export access to the charts directly by iterating over the BlockModels, this is the setter
     */
    public mapBlockToChartComponent(block: BlockModel, chartBlock: ChartBlockComponent): void {
        this.blockChartMap.set(block, chartBlock);
    }

    /**
     * These maps are to give export access to the charts directly by iterating over the BlockModels, this is the getter
     */
    public getChartComponentFromBlock(block: BlockModel): ChartBlockComponent {
        return this.blockChartMap.get(block);
    }
    getDefaultTemplateByBlockType(blockType: string, defaultReference: string): string {
        return '';
    }

    /**
     * Duplicate a block
     *
     * @param block The block to duplicate
     */
    duplicateBlock(block: BlockModel): void {
        const index = this.blocks.indexOf(block);

        if (index < 0) {
            // shouldn't ever happen
            throw new Error('Item not in list');
        }

        const clone = block.clone();
        clone.id = Math.max(...this.currentReport.blocks.map(x => x.id), 0) + 1;
        const displayElement = clone.displayElement as ChartDisplayElementModel;
        displayElement._series.forEach(s => {
            delete s.id;
            delete s.mentions?.searchCriteriaId;
            s.mentions?.filters?.anonymize();
        });
        this.blocks.splice(index, 0, clone);
        this.setBlocks(this.blocks);
    }

    getGridsterAPI(gridster: Gridster) {
        this.gridsterAPI = gridster.api;
    }

    updateBlockOrder() {
        this.blocks.sort((a, b) => {
            if (a.y === b.y) {
                return a.x - b.x;
            }
            return a.y - b.y;
        });
    }

    setMaxMinDimensions(block: BlockModel) {
        block.maxItemCols = 10;
        block.maxItemRows = 6;
        block.minItemCols = 2;
        block.minItemRows = 2;
        switch(block.type.key) {
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                block.minItemRows = 3;
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.TOP_POSTS:
                block.minItemCols = 3;
                break;
            case ReportBlockTypeEnum.PROMINENCE_BY_REACH:
                block.minItemCols = 3;
                block.minItemRows = 3;
                break;
        }

        if (block.maxItemRows && block.rows > block.maxItemRows) {
            block.rows = block.maxItemRows;
        } else if (block.minItemRows && block.rows < block.minItemRows) {
            block.rows = block.minItemRows;
        }

        if (block.maxItemCols && block.cols > block.maxItemCols) {
            block.cols = block.maxItemCols;
        } else if (block.minItemCols && block.cols < block.minItemCols) {
            block.cols = block.minItemCols;
        }
    }

    getResizedBlock(): Observable<BlockModel> {
        return this.resizedBlock$;
    }
}
